import React, { useState } from 'react';
import { IconExternalLink } from '@tabler/icons-react';

// ---------------------------------------------------------------------------
// AllergyBadge
//
// Props:
//   nkda      — boolean from patient record
//   nka       — boolean from patient record
//   allergies — array of real allergy entries (no nkda/nka pseudo-rows)
//   onEdit    — callback to open the AllergyModal
//
// Badge states:
//   Unknown   — nkda=false, nka=false, no allergy entries
//   NKDA      — nkda=true, no drug allergies present
//   NKA       — nka=true, no allergies present
//   Allergies — one or more allergy entries exist
//
// Colors sourced from confirmed Nephros palette (Color System v2.1).
//   Allergies — white #FFFFFF on alert red #B91C1C     (~7.2:1 ✓)
//   NKDA/NKA  — charcoal #1F2937 on light grey #D1D5DB (~10.1:1 ✓)
//   Unknown   — dark amber #92400E on amber wash #FEF3C7 (~5.8:1 ✓)
// ---------------------------------------------------------------------------
export default function AllergyBadge({ nkda, nka, allergies = [], onEdit }) {
  const [hovering, setHovering] = useState(false);

  let label, badgeStyle;

  if (nka) {
    label = 'NKA';
    badgeStyle = styles.allergyNeutral;
  } else if (nkda) {
    label = 'NKDA';
    badgeStyle = styles.allergyNeutral;
  } else if (allergies.length > 0) {
    label = 'Allergies';
    badgeStyle = styles.allergyAlert;
  } else {
    label = 'Allergies Unknown';
    badgeStyle = styles.allergyUnknown;
  }

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <button style={{ ...styles.allergyBadge, ...badgeStyle }} onClick={onEdit}>
        {label}
      </button>

      {/* Tooltip — shown on hover when real allergies exist */}
      {hovering && allergies.length > 0 && (
        <div style={styles.tooltipWrapper}>
          <div style={styles.allergyTooltip} onClick={onEdit}>
            <IconExternalLink
              size={14}
              stroke={1.75}
              style={styles.tooltipExpandIcon}
            />
            {allergies.map((a, i) => (
              <div key={i} style={styles.tooltipItem}>
                <span style={styles.tooltipAllergen}>{a.allergen}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tooltip — shown on hover for NKDA/NKA/Unknown states */}
      {hovering && allergies.length === 0 && (
        <div style={styles.tooltipWrapper}>
          <div style={styles.allergyTooltip} onClick={onEdit}>
            <IconExternalLink
              size={14}
              stroke={1.75}
              style={styles.tooltipExpandIcon}
            />
            <div style={styles.tooltipItem}>
              <span style={styles.tooltipAllergen}>
                {nka
                  ? 'No known allergies of any type'
                  : nkda
                  ? 'No known drug allergies'
                  : 'Allergy status not yet documented'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  allergyBadge: {
    border: 'none',
    borderRadius: 'var(--radius-full)',
    padding: '2px 8px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family-base)',
  },
  allergyNeutral: {
    backgroundColor: '#D1D5DB',
    color: '#1F2937',
  },
  allergyAlert: {
    backgroundColor: '#B91C1C',
    color: '#FFFFFF',
  },
  allergyUnknown: {
    backgroundColor: '#FEF3C7',
    color: '#92400E',
  },

  // --- Tooltip (light card) -----------------------------------------------
  // Wrapper provides a transparent 4px bridge so the cursor can travel from
  // the badge onto the tooltip without crossing a gap that drops :hover.
  tooltipWrapper: {
    position: 'absolute',
    top: '100%',
    left: 0,
    paddingTop: '4px',
    zIndex: 300,
  },
  allergyTooltip: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    padding: '8px 0',
    minWidth: '200px',
    boxShadow: 'var(--shadow-lg)',
    cursor: 'pointer',
  },
  tooltipExpandIcon: {
    position: 'absolute',
    top: '8px',
    right: '10px',
    color: 'var(--color-text-muted)',
  },
  tooltipItem: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-primary)',
    padding: '3px 14px',
    lineHeight: '1.4',
    fontFamily: 'var(--font-family-base)',
  },
  tooltipAllergen: {
    fontWeight: 'var(--font-weight-medium)',
  },
};